import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { secureLog } from '@/lib/security';

interface BackgroundImage {
  id: string;
  image_url: string;
  background_position: string;
  is_active: boolean;
  status: string;
}

export const useBackgroundImage = (page: string = 'home', previewMode: boolean = false, skipLoading: boolean = false) => {
  const [backgroundImage, setBackgroundImage] = useState<BackgroundImage | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadActiveBackgroundImage = async () => {
    if (skipLoading) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // In preview mode, show draft images; otherwise show published
      const status = previewMode ? 'draft' : 'published';
      
      const { data, error } = await supabase
        .from('background_images')
        .select('*')
        .eq('is_active', true)
        .eq('page', page)
        .eq('status', status)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setBackgroundImage(data[0]);
      } else {
        setBackgroundImage(null);
      }
    } catch (error) {
      secureLog.error('Error loading background image', error);
      setBackgroundImage(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Skip loading if disabled
    if (skipLoading) return;
    
    // Load background image immediately
    loadActiveBackgroundImage();

    // Listen for background image changes
    const handleBackgroundChange = () => {
      loadActiveBackgroundImage();
    };

    window.addEventListener('background-changed', handleBackgroundChange);
    return () => {
      window.removeEventListener('background-changed', handleBackgroundChange);
    };
  }, [page, previewMode]);

  return { backgroundImage, isLoading, refetch: loadActiveBackgroundImage };
};